import {
  Alert,
  Backdrop,
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  Icon,
  Snackbar,
  Stack,
} from "@mui/material";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import MDBox from "theme/components/MDBox";
import MDTypography from "theme/components/MDTypography";
import DashboardLayout from "app/LayoutContainers/DashboardLayout";
import DashboardNavbar from "app/Navbars/DashboardNavbar";
import Footer from "theme/Footer";
import usePageTitle from "app/pageTitle";
import axios from "axios.js";

export default function LoyaltyConsumerPage() {
  usePageTitle("Consumer Profile");

  const { ref } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [errMsg, setErrMsg] = useState("");
  const [consumer, setConsumer] = useState(null);

  useEffect(() => {
    const fetchConsumer = async () => {
      setLoading(true);

      try {
        const res = await axios.get(`/portal/tm/loyalty/promotions/consumers/${ref}/fetch.json`);

        if (res.data.status === "success") {
          setConsumer(res.data.data.consumer);
        } else {
          setErrMsg(res.data.data.message || "Failed to load consumer.");
          setOpen(true);
        }
      } catch (error) {
        setErrMsg(error.response?.data?.data?.message || "Failed to load consumer.");
        setOpen(true);
      } finally {
        setLoading(false);
      }
    };

    if (ref) fetchConsumer();
  }, [ref]);

  const formatDate = (value) => {
    if (!value) return "---";

    const date = new Date(value);

    if (Number.isNaN(date.getTime())) return value;

    return date.toLocaleString("en-NG", {
      dateStyle: "medium",
      timeStyle: "short",
    });
  };

  const DetailItem = ({ label, value, icon, secondary }) => (
    <Box sx={{ minWidth: 0 }}>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
        {icon && <Icon sx={{ fontSize: 18, color: "#667085" }}>{icon}</Icon>}
        <MDTypography
          variant="caption"
          color="text"
          sx={{
            textTransform: "uppercase",
            letterSpacing: 0.5,
            fontSize: "0.68rem",
            opacity: 0.7,
          }}
        >
          {label}
        </MDTypography>
      </Stack>

      <MDTypography
        variant="button"
        fontWeight="medium"
        sx={{
          display: "block",
          overflowWrap: "anywhere",
          lineHeight: 1.7,
        }}
      >
        {value === null || value === undefined || value === "" ? "---" : value}
      </MDTypography>

      {secondary && (
        <MDTypography
          variant="caption"
          color="text"
          sx={{ display: "block", mt: 0.5, opacity: 0.65 }}
        >
          {secondary}
        </MDTypography>
      )}
    </Box>
  );

  const SectionTitle = ({ icon, title, subtitle }) => (
    <Stack direction="row" spacing={1.5} alignItems="center" mb={3}>
      <Box
        sx={{
          width: 42,
          height: 42,
          borderRadius: "10px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: "#f0f2f5",
          color: "#344054",
          flexShrink: 0,
        }}
      >
        <Icon fontSize="medium">{icon}</Icon>
      </Box>

      <Box sx={{ minWidth: 0 }}>
        <MDTypography variant="h6" fontWeight="bold">
          {title}
        </MDTypography>

        {subtitle && (
          <MDTypography
            variant="button"
            color="text"
            sx={{ display: "block", opacity: 0.65, mt: 0.25 }}
          >
            {subtitle}
          </MDTypography>
        )}
      </Box>
    </Stack>
  );

  const fullName =
    `${consumer?.first_name || ""} ${consumer?.last_name || ""}`.trim() || "Consumer";

  const initials =
    `${consumer?.first_name?.charAt(0) || ""}${
      consumer?.last_name?.charAt(0) || ""
    }`.toUpperCase() || "C";

  return (
    <DashboardLayout>
      <DashboardNavbar />

      <Backdrop
        sx={{
          color: "#fff",
          zIndex: (theme) => theme.zIndex.drawer + 1,
        }}
        open={loading}
      >
        <CircularProgress color="inherit" />
      </Backdrop>

      <Snackbar
        open={open}
        autoHideDuration={6000}
        onClose={() => setOpen(false)}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert onClose={() => setOpen(false)} severity="error" sx={{ width: "100%" }}>
          {errMsg}
        </Alert>
      </Snackbar>

      <MDBox mt={2} mb={4}>
        <Button
          variant="outlined"
          startIcon={<Icon>arrow_back</Icon>}
          onClick={() => navigate(-1)}
          sx={{
            mb: 2,
            textTransform: "none",
            borderColor: "#d0d5dd",
            color: "#344054",
            borderRadius: "8px",
            "&:hover": {
              borderColor: "#98a2b3",
              backgroundColor: "#f8f9fb",
            },
          }}
        >
          Back to consumers
        </Button>

        {!loading && !consumer && (
          <Card
            sx={{
              p: 4,
              textAlign: "center",
              borderRadius: "12px",
              boxShadow: "none",
              border: "1px solid #e8eaf0",
            }}
          >
            <Icon sx={{ fontSize: 44, color: "#98a2b3", mb: 1 }}>person_off</Icon>

            <MDTypography variant="h6" fontWeight="bold">
              Consumer unavailable
            </MDTypography>

            <MDTypography variant="button" color="text" sx={{ display: "block", mt: 1 }}>
              Consumer details could not be displayed.
            </MDTypography>
          </Card>
        )}

        {consumer && (
          <Stack spacing={2.5}>
            {/* Consumer header */}
            <Card
              sx={{
                borderRadius: "12px",
                boxShadow: "none",
                border: "1px solid #e8eaf0",
                overflow: "hidden",
              }}
            >
              <Box
                sx={{
                  p: { xs: 2.5, md: 3.5 },
                  background: "linear-gradient(120deg, #202936 0%, #344054 100%)",
                  color: "#fff",
                }}
              >
                <Stack
                  direction={{ xs: "column", sm: "row" }}
                  alignItems={{ xs: "flex-start", sm: "center" }}
                  justifyContent="space-between"
                  spacing={2}
                >
                  <Stack direction="row" spacing={2} alignItems="center" sx={{ minWidth: 0 }}>
                    <Box
                      sx={{
                        width: 68,
                        height: 68,
                        borderRadius: "50%",
                        bgcolor: "rgba(255,255,255,0.12)",
                        border: "1px solid rgba(255,255,255,0.2)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                      }}
                    >
                      <MDTypography variant="h4" fontWeight="bold" sx={{ color: "#fff" }}>
                        {initials}
                      </MDTypography>
                    </Box>

                    <Box sx={{ minWidth: 0 }}>
                      <MDTypography
                        variant="caption"
                        sx={{
                          color: "rgba(255,255,255,0.7)",
                          letterSpacing: 1,
                          fontWeight: "medium",
                        }}
                      >
                        LOYALTY CONSUMER
                      </MDTypography>

                      <MDTypography
                        variant="h4"
                        fontWeight="bold"
                        sx={{
                          color: "#fff",
                          mt: 0.5,
                          overflowWrap: "anywhere",
                          fontSize: { xs: "1.5rem", md: "2rem" },
                        }}
                      >
                        {fullName}
                      </MDTypography>

                      <MDTypography
                        variant="button"
                        sx={{
                          display: "block",
                          mt: 0.75,
                          color: "rgba(255,255,255,0.75)",
                          overflowWrap: "anywhere",
                        }}
                      >
                        {consumer.email_address || "No email address"}
                      </MDTypography>
                    </Box>
                  </Stack>

                  <Chip
                    icon={
                      <Icon
                        sx={{
                          color: "#067647 !important",
                          fontSize: "18px !important",
                        }}
                      >
                        verified_user
                      </Icon>
                    }
                    label="Registered consumer"
                    sx={{
                      bgcolor: "#d1fadf",
                      color: "#067647",
                      fontWeight: 700,
                      borderRadius: "8px",
                      height: 36,
                    }}
                  />
                </Stack>
              </Box>

              <Box sx={{ p: { xs: 2, md: 3 } }}>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6} md={4}>
                    <Box
                      sx={{
                        p: 2,
                        height: "100%",
                        border: "1px solid #eaecf0",
                        borderRadius: "10px",
                      }}
                    >
                      <DetailItem
                        label="Email address"
                        value={consumer.email_address}
                        icon="mail_outline"
                        secondary="Consumer contact email"
                      />
                    </Box>
                  </Grid>

                  <Grid item xs={12} sm={6} md={4}>
                    <Box
                      sx={{
                        p: 2,
                        height: "100%",
                        border: "1px solid #eaecf0",
                        borderRadius: "10px",
                      }}
                    >
                      <DetailItem
                        label="Phone number"
                        value={consumer.phone_number}
                        icon="phone"
                        secondary="Consumer contact number"
                      />
                    </Box>
                  </Grid>

                  <Grid item xs={12} sm={6} md={4}>
                    <Box
                      sx={{
                        p: 2,
                        height: "100%",
                        border: "1px solid #eaecf0",
                        borderRadius: "10px",
                      }}
                    >
                      <DetailItem
                        label="Agent ID"
                        value={consumer.agent_id}
                        icon="badge"
                        secondary="Registering agent"
                      />
                    </Box>
                  </Grid>
                </Grid>
              </Box>
            </Card>

            {/* Registration details */}
            <Card
              sx={{
                p: { xs: 2, md: 3 },
                borderRadius: "12px",
                boxShadow: "none",
                border: "1px solid #e8eaf0",
              }}
            >
              <SectionTitle
                icon="assignment_ind"
                title="Registration details"
                subtitle="Promotion and registration information for this consumer."
              />

              <Divider sx={{ mb: 3 }} />

              <Grid container spacing={3}>
                <Grid item xs={12} sm={6} md={4}>
                  <DetailItem label="Consumer name" value={fullName} icon="person_outline" />
                </Grid>

                <Grid item xs={12} sm={6} md={4}>
                  <DetailItem label="Promotion" value={consumer.promotion_title} icon="campaign" />
                </Grid>

                <Grid item xs={12} sm={6} md={4}>
                  <DetailItem
                    label="Registration date"
                    value={formatDate(consumer.created)}
                    icon="calendar_month"
                  />
                </Grid>

                <Grid item xs={12} sm={6} md={4}>
                  <DetailItem label="Agent ID" value={consumer.agent_id} icon="badge" />
                </Grid>
              </Grid>
            </Card>
          </Stack>
        )}
      </MDBox>

      <Footer />
    </DashboardLayout>
  );
}
